/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1720396800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Mon Jul 08 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "google-bigquery",
        type: "source",
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "BigQuery credential setup",
          "depth": 0,
          "id": "bigquery-credential-setup"
        }, {
          "value": "Create a project and enable BigQuery API",
          "depth": 1,
          "id": "create-a-project-and-enable-bigquery-api"
        }, {
          "value": "Create a service account",
          "depth": 1,
          "id": "create-a-service-account"
        }, {
          "value": "Grant access",
          "depth": 1,
          "id": "grant-access"
        }, {
          "value": "Connection configuration",
          "depth": 0,
          "id": "connection-configuration"
        }, {
          "value": "Configure your service account",
          "depth": 1,
          "id": "configure-your-service-account"
        }, {
          "value": "Configure your source",
          "depth": 1,
          "id": "configure-your-source"
        }, {
          "value": "Choose your sync engine",
          "depth": 1,
          "id": "choose-your-sync-engine"
        }, {
          "value": "Test your connection",
          "depth": 0,
          "id": "test-your-connection"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Hashed values are displayed as objects of bytes",
          "depth": 1,
          "id": "hashed-values-are-displayed-as-objects-of-bytes"
        }, {
          "value": "No matching signature for operator {...} for argument types: {...}, {...}.",
          "depth": 1,
          "id": "no-matching-signature-for-operator--for-argument-types--"
        }, {
          "value": "Unable to process number due to [big.js]",
          "depth": 1,
          "id": "unable-to-process-number-due-to-bigjs"
        }, {
          "value": "Dataset was not found in location",
          "depth": 1,
          "id": "dataset-was-not-found-in-location"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      p: "p",
      ol: "ol",
      li: "li",
      h3: "h3",
      a: "a",
      ul: "ul",
      strong: "strong",
      code: "code",
      img: "img",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {SyncEngineSnippet, EngineComparisonTable, SourceTestSnippet, SyncLogsSnippet, CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EngineComparisonTable) _missingMdxReference("EngineComparisonTable", true);
    if (!SourceTestSnippet) _missingMdxReference("SourceTestSnippet", true);
    if (!SyncEngineSnippet) _missingMdxReference("SyncEngineSnippet", true);
    if (!SyncLogsSnippet) _missingMdxReference("SyncLogsSnippet", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you pull data stored in your Google BigQuery warehouse and push it to downstream destinations."
      }), "\n", _jsx(_components.p, {
        children: "Connecting Hightouch to BigQuery requires some setup in both platforms. It's recommended to set up a service account with the correct permissions in BigQuery before configuring the connection in Hightouch."
      }), "\n", _jsx(WhitelistDetails, {}), "\n", _jsx(_components.h2, {
        id: "bigquery-credential-setup",
        children: "BigQuery credential setup"
      }), "\n", _jsx(_components.p, {
        children: "Setup in BigQuery has three main steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Enable BigQuery for your Google Cloud project"
        }), "\n", _jsx(_components.li, {
          children: "Create a service account"
        }), "\n", _jsx(_components.li, {
          children: "Grant the Hightouch service account access to your project"
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "create-a-project-and-enable-bigquery-api",
        children: "Create a project and enable BigQuery API"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Login to the ", _jsx(_components.a, {
            href: "https://console.developers.google.com/",
            children: "Google Developers Console"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Configure the ", _jsx(_components.a, {
            href: "https://console.cloud.google.com/",
            children: "Cloud Platform"
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["If you don't have a project already, ", _jsx(_components.a, {
                href: "https://support.google.com/cloud/answer/6251787?hl=en&ref_topic=6158848",
                children: "create one"
              }), "."]
            }), "\n", _jsxs(_components.li, {
              children: ["Once you have a project, ", _jsx(_components.a, {
                href: "https://console.cloud.google.com/flows/enableapi?apiid=bigquery&_ga=2.71379221.724057513.1673650275-1611021579.1664923822&_gac=1.213641504.1673650813.EAIaIQobChMIt9GagtPF_AIVkgB9Ch331QRREAAYASAAEgJfrfD_BwE",
                children: "enable the BigQuery API"
              }), " for it."]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["Copy your ", _jsx(_components.strong, {
            children: "Project ID"
          }), " for later use."]
        }), "\n", _jsxs(_components.li, {
          children: ["Find the ", _jsx(_components.strong, {
            children: "location"
          }), " of your BigQuery dataset or sets. You can find this by querying the ", _jsx(_components.code, {
            children: "INFORMATION_SCHEMA.SCHEMATA"
          }), " view or by visiting the ", _jsx(_components.a, {
            href: "https://console.cloud.google.com/",
            children: "Google Cloud web console"
          }), " and clicking on a BigQuery dataset in the Explorer panel. You need both ", _jsx(_components.strong, {
            children: "Project ID"
          }), " and ", _jsx(_components.strong, {
            children: "Data location"
          }), " when connecting Hightouch to BigQuery."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "sources/source-bigquery-data-location.png",
          alt: "Dataset Location in the Google Cloud Console"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Make sure ", _jsx(_components.a, {
            href: "https://support.google.com/cloud/answer/6293499#enable-billing",
            children: "billing is enabled"
          }), " on your project, otherwise Hightouch can't write into the cluster."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "create-a-service-account",
        children: "Create a service account"
      }), "\n", _jsxs(_components.p, {
        children: ["To create a service account, follow the setup instructions in our ", _jsx(_components.a, {
          href: "/security/gcp",
          children: "Google Cloud Provider (GCP) documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "grant-access",
        children: "Grant access"
      }), "\n", _jsxs(_components.p, {
        children: ["By default, your GCP service account doesn't have ", _jsx(_components.a, {
          href: "https://cloud.google.com/bigquery/docs/access-control#permissions-predefined-roles",
          children: "permission to read data"
        }), " from BigQuery.\nYou can set up your service account to have ", _jsx(_components.a, {
          href: "#grant-full-access",
          children: "full access"
        }), " to your project using a ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/understanding-roles#predefined_roles",
          children: "predefined role"
        }), ".\nOtherwise, you can create a ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/roles-overview#custom",
          children: "custom role"
        }), " and provide ", _jsx(_components.a, {
          href: "#grant-limited-access",
          children: "limited access"
        }), " according to a user-specified list of permissions."]
      }), "\n", _jsx(_components.h4, {
        id: "grant-full-access",
        children: "Grant full access"
      }), "\n", _jsxs(_components.p, {
        children: ["You can grant full access by assigning the ", _jsx(_components.code, {
          children: "bigquery.user"
        }), " and ", _jsx(_components.code, {
          children: "bigquery.dataViewer"
        }), " roles to your service account.\nYou can do this in the ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/granting-changing-revoking-access#grant-single-role",
          children: "Google Cloud web console"
        }), " or by running these snippets in the ", _jsx(_components.a, {
          href: "https://cloud.google.com/shell/docs/run-gcloud-commands",
          children: "Cloud Shell"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Grant permission to ", _jsx(_components.strong, {
          children: "read metadata and list tables"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "gcloud projects add-iam-policy-binding <YOUR_PROJECT_NAME> \\\n--member serviceAccount:<YOUR_SERVICE_ACCOUNT> \\\n--role roles/bigquery.user\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Grant permission to ", _jsx(_components.strong, {
          children: "read data from tables and views"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "gcloud projects add-iam-policy-binding <YOUR_PROJECT_NAME> \\\n--member serviceAccount:<YOUR_SERVICE_ACCOUNT> \\\n--role roles/bigquery.dataViewer\n"
        })
      }), "\n", _jsx(_components.h4, {
        id: "grant-limited-access",
        children: "Grant limited access"
      }), "\n", _jsxs(_components.p, {
        children: ["If you don't want to grant full access to your BigQuery service account, you can opt to grant limited access instead.\nYou can do this by assigning the ", _jsx(_components.code, {
          children: "bigquery.dataViewer"
        }), " role only to the specific datasets, tables, or views you want to use in Hightouch."]
      }), "\n", _jsxs(_components.p, {
        children: ["Since you are assigning the ", _jsx(_components.code, {
          children: "bigquery.dataViewer"
        }), " role only to specific resources, you need to assign the ", _jsx(_components.code, {
          children: "bigquery.user"
        }), " role ", _jsx(_components.strong, {
          children: "and"
        }), " grant the ", _jsx(_components.code, {
          children: "bigquery.tables.get"
        }), " permission at the project level.\nFor this, you can create a ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/creating-custom-roles#creating",
          children: "custom role"
        }), " in the Google Cloud web console based on an ", _jsx(_components.a, {
          href: "https://cloud.google.com/bigquery/docs/access-control#bigquery.user",
          children: "existing predefined role"
        }), " (", _jsx(_components.code, {
          children: "bigquery.user"
        }), "), which you can name ", _jsx(_components.code, {
          children: "custom.bigquery.user"
        }), ".\nWhen setting up the custom role, click ", _jsx(_components.strong, {
          children: "Add permissions"
        }), "  to add the ", _jsx(_components.code, {
          children: "bigquery.tables.get"
        }), " permission to this custom role."]
      }), "\n", _jsxs(_components.p, {
        children: ["Then assign this role to your service account at the project level.\nYou can do this in the ", _jsx(_components.a, {
          href: "https://cloud.google.com/iam/docs/granting-changing-revoking-access#grant-single-role",
          children: "Google Cloud web console"
        }), " or by running this snippet in the ", _jsx(_components.a, {
          href: "https://cloud.google.com/shell/docs/run-gcloud-commands",
          children: "Cloud Shell"
        }), ":"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "gcloud projects add-iam-policy-binding <YOUR_PROJECT_NAME> \\\n--member serviceAccount:<YOUR_SERVICE_ACCOUNT> \\\n--role roles/custom.bigquery.user\n"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["If this ", _jsx(_components.code, {
            children: "custom.bigquery.user"
          }), " role still isn't limited enough, you can try assigning the ", _jsx(_components.a, {
            href: "https://cloud.google.com/bigquery/docs/access-control#bigquery.jobUser",
            children: _jsx(_components.code, {
              children: "bigquery.jobUser"
            })
          }), " role ", _jsx(_components.strong, {
            children: "and"
          }), " granting the ", _jsx(_components.code, {
            children: "bigquery.dataset.get"
          }), ", ", _jsx(_components.code, {
            children: "bigquery.tables.get"
          }), ", and ", _jsx(_components.code, {
            children: "bigquery.tables.list"
          }), " permissions at the project level."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch needs to be able to list the schemas and tables in your BigQuery project when ", _jsx(_components.a, {
            href: "/models/creating-models",
            children: "creating a model"
          }), ".\nTherefore, the ", _jsx(_components.a, {
            href: "#test-your-connection",
            children: "source connection test"
          }), " fails if you don't grant the ", _jsx(_components.code, {
            children: "bigquery.tables.get"
          }), " permission at the project level."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can then decide which datasets, tables, or views your GCP service account has access to by ", _jsx(_components.a, {
          href: "https://cloud.google.com/bigquery/docs/control-access-to-resources-iam#grant_access_to_a_resource",
          children: "granting access to a resource"
        }), " in the Google Cloud web console.\nFor every resource you would like to use in Hightouch, select your BigQuery service account as the ", _jsx(_components.strong, {
          children: "Principal"
        }), " and the ", _jsx(_components.code, {
          children: "bigquery.dataViewer"
        }), " role as the ", _jsx(_components.strong, {
          children: "Role"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Hightouch lists all tables in your BigQuery project when creating a model using the ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          }), ".\nHowever, Hightouch can only query data from tables assigned the ", _jsx(_components.code, {
            children: "bigquery.dataViewer"
          }), " role.\nTables that weren't assigned this role return an error if you attempt to query them."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "connection-configuration",
        children: "Connection configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["To get started, go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/sources",
          children: [_jsx(_components.strong, {
            children: "Sources"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add source"
        }), " button. Select ", _jsx(_components.strong, {
          children: "BigQuery"
        }), " and follow the steps below."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-service-account",
        children: "Configure your service account"
      }), "\n", _jsxs(_components.p, {
        children: ["Select the ", _jsx(_components.strong, {
          children: "GCP credentials"
        }), " you ", _jsx(_components.a, {
          href: "#create-a-service-account",
          children: "previously created"
        }), " or click ", _jsx(_components.strong, {
          children: "Create new"
        }), ". To learn more about these credentials, see the ", _jsx(_components.a, {
          href: "/security/gcp",
          children: "Google Cloud Provider (GCP) documentation"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "configure-your-source",
        children: "Configure your source"
      }), "\n", _jsxs(_components.p, {
        children: ["Enter the ", _jsx(_components.strong, {
          children: "Project ID"
        }), " for the project you ", _jsx(_components.a, {
          href: "#create-a-project-and-enable-bigquery-api",
          children: "enabled the BigQuery API"
        }), " for and the ", _jsx(_components.strong, {
          children: "Dataset location"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "choose-your-sync-engine",
        children: "Choose your sync engine"
      }), "\n", _jsx(SyncEngineSnippet, {
        source: "BigQuery"
      }), "\n", _jsx(_components.h4, {
        id: "basic-versus-lightning-engine-comparison",
        children: "Basic versus Lightning engine comparison"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), " requires granting write access to your data warehouse, which makes its setup more involved than the Basic sync engine. However, it is more performant and reliable than the Basic engine. This makes it the ideal choice to guarantee faster syncs, especially with large data models. It also supports more features, such as ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), ", ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), ", and ", _jsx(_components.a, {
          href: "https://hightouch.com/platform/identity-resolution",
          children: "Identity Resolution"
        }), "."]
      }), "\n", _jsx(EngineComparisonTable, {
        source: "BigQuery"
      }), "\n", _jsx(_components.h4, {
        id: "lightning-engine-permissions",
        children: "Lightning engine permissions"
      }), "\n", _jsx(_components.p, {
        children: "To enable the Lightning engine, you need to provide your service account additional permissions to create schemas read/write data."
      }), "\n", _jsxs(_components.p, {
        children: ["Run the following snippet to provision the ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " and ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), " schemas, which are used for storing logs of previously synced data."]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " `hightouch_audit`;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "CREATE"
          }), " SCHEMA IF ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "NOT"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "EXISTS"
          }), " `hightouch_planner`;\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " `roles", _jsx(_components.span, {
            className: "hljs-operator",
            children: "/"
          }), "bigquery.dataViewer`, `roles", _jsx(_components.span, {
            className: "hljs-operator",
            children: "/"
          }), "bigquery.dataEditor` ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA `hightouch_planner` ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " \"serviceAccount:<YOUR_SERVICE_ACCOUNT>\";\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "GRANT"
          }), " `roles", _jsx(_components.span, {
            className: "hljs-operator",
            children: "/"
          }), "bigquery.dataViewer`, `roles", _jsx(_components.span, {
            className: "hljs-operator",
            children: "/"
          }), "bigquery.dataEditor` ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "ON"
          }), " SCHEMA `hightouch_audit` ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "TO"
          }), " \"serviceAccount:<YOUR_SERVICE_ACCOUNT>\";\n"]
        })
      }), "\n", _jsx(_components.h2, {
        id: "test-your-connection",
        children: "Test your connection"
      }), "\n", _jsx(SourceTestSnippet, {
        source: "BigQuery"
      }), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.p, {
        children: ["Once your source configuration has passed the necessary validation, your source setup is complete. Next, you can set up ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "models"
        }), " to define which data you want to pull from BigQuery."]
      }), "\n", _jsx(_components.p, {
        children: "The BigQuery source supports these modeling methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["writing a query in ", _jsx(_components.a, {
            href: "/models/sql-editor",
            children: "the SQL editor"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["using the visual ", _jsx(_components.a, {
            href: "/models/table-selector",
            children: "table selector"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/dbt-models",
            children: "dbt models"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/looker-models",
            children: "Looker Looks"
          })]
        }), "\n", _jsxs(_components.li, {
          children: ["leveraging existing ", _jsx(_components.a, {
            href: "/extensions/sigma",
            children: "Sigma workbooks"
          })]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "SQL editor"
        }), " allows you to query data from all projects that your GCP service account ", _jsx(_components.a, {
          href: "#create-a-service-account",
          children: "has access to"
        }), ".\nThe ", _jsx(_components.a, {
          href: "/models/table-selector",
          children: "table selector"
        }), " only supports querying from the project specified in the ", _jsx(_components.a, {
          href: "#configure-your-source",
          children: "source configuration"
        }), "."]
      }), "\n", _jsx(Tip, {
        children: _jsx(_components.p, {
          children: "When syncing large amounts of data, it can take a long time for your model to reflect changes made in BigQuery. To speed up BigQuery model updates in the Hightouch UI, you can preview a model and save it. New or updated columns should then be reflected in the Hightouch UI."
        })
      }), "\n", _jsx(SyncLogsSnippet, {
        source: "BigQuery"
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {
        source: "true"
      }), "\n", _jsx(_components.h3, {
        id: "hashed-values-are-displayed-as-objects-of-bytes",
        children: "Hashed values are displayed as objects of bytes"
      }), "\n", _jsxs(_components.p, {
        children: ["When ", _jsx(_components.a, {
          href: "/models/creating-models#preview-model-results",
          children: "previewing your model"
        }), " or when syncing your data to the destination, you might see an object of bytes, such as:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-bash",
          children: ["{", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"0\""
          }), ":123,", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"1\""
          }), ":456,", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"2\""
          }), ":789,", _jsx(_components.span, {
            className: "hljs-string",
            children: "\"3\""
          }), ":012,...}\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["This happens if you use certain ", _jsx(_components.a, {
          href: "https://cloud.google.com/bigquery/docs/reference/standard-sql/hash_functions",
          children: "hashing algorithms"
        }), " in BigQuery to hash your data, such as ", _jsx(_components.a, {
          href: "https://cloud.google.com/bigquery/docs/reference/standard-sql/hash_functions#sha256",
          children: "SHA256"
        }), ". To resolve this, open the ", _jsx(_components.a, {
          href: "/models/sql-editor",
          children: "SQL editor"
        }), " and use ", _jsx(_components.a, {
          href: "https://cloud.google.com/bigquery/docs/reference/standard-sql/string_functions#to_hex",
          children: "TO_HEX"
        }), " or ", _jsx(_components.a, {
          href: "https://cloud.google.com/bigquery/docs/reference/standard-sql/string_functions#to_base64",
          children: "TO_BASE64"
        }), " to query this model column:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "SELECT TO_HEX(model_column_name) as string_model_column_name, * FROM dataset.table\n"
        })
      }), "\n", _jsx(_components.h3, {
        id: "no-matching-signature-for-operator--for-argument-types--",
        children: "No matching signature for operator {...} for argument types: {...}, {...}."
      }), "\n", _jsxs(_components.p, {
        children: ["An example of the error message is ", _jsx(_components.code, {
          children: "No matching signature for operator != for argument types: DATE, TIMESTAMP."
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["This error means that your ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model definition"
        }), " is using the named operator, for example, ", _jsx(_components.code, {
          children: "!="
        }), " to compare two incompatible types, for example, a ", _jsx(_components.code, {
          children: "date"
        }), " to a ", _jsx(_components.code, {
          children: "timestamp"
        }), ".\nTo resolve the issue, ensure your model columns are properly ", _jsx(_components.a, {
          href: "/models/data-types-casting#casting-from-your-model-configuration",
          children: "typed"
        }), " or revise your model query."]
      }), "\n", _jsx(_components.h3, {
        id: "unable-to-process-number-due-to-bigjs",
        children: "Unable to process number due to [big.js]"
      }), "\n", _jsxs(_components.p, {
        children: ["An example of the error message is ", _jsx(_components.code, {
          children: "Unable to process number due to [big.js] Imprecise conversion: [big.js] Imprecise conversion"
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["This error occurs when one or more values have a very high precision, for example ", _jsx(_components.code, {
          children: "123.12345678910"
        }), ". Hightouch attempts to convert the value to a ", _jsx(_components.code, {
          children: "number"
        }), " (based on the data type selection of your model). An error occurs if the number can't be precisely represented by a JavaScript number."]
      }), "\n", _jsxs(_components.p, {
        children: ["To resolve the issue, ensure the values in your model columns have a lower precision. Additionally, if your value doesn't need to be a ", _jsx(_components.code, {
          children: "number"
        }), " datatype, casting the value to a ", _jsx(_components.code, {
          children: "string"
        }), " datatype can also resolve this issue."]
      }), "\n", _jsx(_components.h3, {
        id: "dataset-was-not-found-in-location",
        children: "Dataset was not found in location"
      }), "\n", _jsxs(_components.p, {
        children: ["You may encounter this error if you customized the lightning cache location. You must use fully qualified table names - ", _jsx(_components.code, {
          children: "project_id.dataset.table"
        }), " in model queries. Note that you cannot use dbt, Looker, Sigma or the Table Selector to create models when a custom project is specified for the lightning cache. You will have to use the SQL query editor for the model query."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
